/**
 * useGenerationPrompt - Comprehensive React hook for managing generationPrompt state and operations
 *
 * This hook provides a complete interface for handling generation prompts in ContentNode components,
 * including state management, validation, accessibility, and performance optimizations.
 *
 * Features:
 * - State management for prompt display, truncation, copy status, and error states
 * - Clipboard operations with user feedback
 * - Prompt validation and format checking with error handling
 * - Accessibility helpers for screen reader announcements
 * - Performance optimizations with useMemo and useCallback
 * - Debug logging and monitoring integration
 * - Support for different display modes (full, truncated, preview)
 * - State persistence for user preferences
 * - Debouncing for performance-heavy operations
 *
 * @author Generated for Tutr Frontend
 * @version 1.0.0
 */import{useState,useEffect,useMemo,useCallback,useRef}from'react';import{validatePromptData,sanitizePrompt,truncatePrompt,formatPromptForDisplay,copyPromptToClipboard,analyzePromptCached,isPromptExpected,getPromptDisplayStatus,getPromptAccessibilityDescription}from"../utils/generationPromptUtils";// ============================================================================
// Type Definitions
// ============================================================================
/**
 * Display modes for prompt rendering
 *//**
 * Copy operation status with enhanced feedback
 *//**
 * Prompt editing state for future extensibility
 *//**
 * User preferences for prompt display
 *//**
 * Error information for prompt operations
 *//**
 * Performance metrics for monitoring
 *//**
 * Accessibility information for screen readers
 *//**
 * Complete return type for useGenerationPrompt hook
 *//**
 * Configuration options for the hook
 */// ============================================================================
// Default Values and Constants
// ============================================================================
const DEFAULT_PREFERENCES={defaultDisplayMode:'truncated',autoExpand:false,truncationLength:300,showAnalysis:false};const DEFAULT_OPTIONS={enableEditing:false,preferences:{},debug:process.env.NODE_ENV==='development',truncationLength:300,debounceDelay:300,enableMetrics:true};// ============================================================================
// Utility Functions
// ============================================================================
/**
 * Debounce utility for performance optimization
 */function useDebounce(value,delay){const[debouncedValue,setDebouncedValue]=useState(value);useEffect(()=>{const timer=setTimeout(()=>setDebouncedValue(value),delay);return()=>clearTimeout(timer);},[value,delay]);return debouncedValue;}/**
 * Performance timing utility
 */function usePerformanceTimer(){return useCallback(operation=>{const start=performance.now();return()=>{const end=performance.now();const duration=end-start;if(process.env.NODE_ENV==='development'){console.debug(`[useGenerationPrompt] ${operation}: ${duration.toFixed(2)}ms`);}return duration;};},[]);}/**
 * Local storage utility for preferences
 */function useLocalStorage(key,defaultValue){const[value,setValue]=useState(()=>{try{if(typeof window==='undefined')return defaultValue;const item=window.localStorage.getItem(key);return item?JSON.parse(item):defaultValue;}catch(error){console.warn(`Failed to load ${key} from localStorage:`,error);return defaultValue;}});const setStoredValue=useCallback(newValue=>{try{setValue(newValue);if(typeof window!=='undefined'){window.localStorage.setItem(key,JSON.stringify(newValue));}}catch(error){console.warn(`Failed to save ${key} to localStorage:`,error);}},[key]);return[value,setStoredValue];}// ============================================================================
// Main Hook Implementation
// ============================================================================
/**
 * useGenerationPrompt - Main hook for comprehensive prompt management
 * 
 * @param nodeData - Content node data containing the generation prompt
 * @param options - Configuration options for the hook
 * @returns Complete prompt management interface
 * 
 * @example
 * ```typescript
 * function MyComponent({ nodeData }: { nodeData: ContentNodeData }) {
 *   const prompt = useGenerationPrompt(nodeData, {
 *     enableEditing: true,
 *     debug: true,
 *     truncationLength: 200
 *   });
 * 
 *   if (!prompt.hasValidPrompt) {
 *     return <div>No prompt available</div>;
 *   }
 * 
 *   return (
 *     <div>
 *       <h3>Generation Prompt</h3>
 *       <pre>{prompt.displayPrompt}</pre>
 *       <button onClick={prompt.copyToClipboard}>
 *         {prompt.copyStatus === 'success' ? 'Copied!' : 'Copy'}
 *       </button>
 *       {prompt.displayMode === 'truncated' && (
 *         <button onClick={prompt.toggleExpanded}>
 *           {prompt.isExpanded ? 'Show Less' : 'Show More'}
 *         </button>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */export function useGenerationPrompt(nodeData){let options=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};// Merge options with defaults
const config=useMemo(()=>({...DEFAULT_OPTIONS,...options}),[options]);// Performance timing
const timer=usePerformanceTimer();// Persistent preferences
const[preferences,setPreferences]=useLocalStorage('generationPrompt.preferences',{...DEFAULT_PREFERENCES,...config.preferences});// Core state
const[displayMode,setDisplayMode]=useState(preferences.defaultDisplayMode);const[isExpanded,setIsExpanded]=useState(false);const[copyStatus,setCopyStatus]=useState('idle');const[error,setError]=useState(null);const[renderCount,setRenderCount]=useState(0);// Edit state (for future editing functionality)
const[editState,setEditState]=useState(config.enableEditing?{isEditing:false,draft:'',hasChanges:false,saving:false}:null);// Performance metrics
const[metrics,setMetrics]=useState({analysisTime:0,validationTime:0,renderCount:0});// Refs for cleanup and state management
const abortControllerRef=useRef(null);const copyTimeoutRef=useRef(null);const liveRegionRef=useRef(null);// Extract raw prompt with performance timing
const rawPrompt=useMemo(()=>{const end=timer('extract-raw-prompt');const result=(nodeData===null||nodeData===void 0?void 0:nodeData.generationPrompt)||null;end();return result;},[nodeData===null||nodeData===void 0?void 0:nodeData.generationPrompt,timer]);// Debounce raw prompt for expensive operations
const debouncedRawPrompt=useDebounce(rawPrompt,config.debounceDelay);// Validate prompt with caching
const validation=useMemo(()=>{if(!debouncedRawPrompt){return{isValid:false,issues:['No prompt provided'],warnings:[]};}const end=timer('validate-prompt');const isValid=validatePromptData(debouncedRawPrompt);const duration=end();setMetrics(prev=>({...prev,validationTime:duration}));if(!isValid){return{isValid:false,issues:[`Invalid prompt format: ${typeof debouncedRawPrompt}`],warnings:[]};}// Additional validation checks
const issues=[];const warnings=[];if(typeof debouncedRawPrompt==='string'){if(debouncedRawPrompt.trim().length===0){issues.push('Prompt is empty or contains only whitespace');}if(debouncedRawPrompt.includes('\x00')){warnings.push('Contains null characters');}if(debouncedRawPrompt.includes('undefined')){warnings.push('Contains "undefined" text');}if(debouncedRawPrompt.length>10000){warnings.push('Extremely long prompt (>10k characters)');}}return{isValid:issues.length===0,issues,warnings};},[debouncedRawPrompt,timer]);// Process and sanitize prompt
const processedPrompt=useMemo(()=>{if(!validation.isValid||!debouncedRawPrompt)return'';const end=timer('process-prompt');const result=sanitizePrompt(formatPromptForDisplay(debouncedRawPrompt));end();return result;},[debouncedRawPrompt,validation.isValid,timer]);// Analyze prompt with caching
const analysis=useMemo(()=>{if(!processedPrompt)return null;const end=timer('analyze-prompt');const result=analyzePromptCached(processedPrompt);const duration=end();setMetrics(prev=>({...prev,analysisTime:duration}));return result;},[processedPrompt,timer]);// Generate display prompt based on mode
const displayPrompt=useMemo(()=>{if(!processedPrompt)return'';switch(displayMode){case'preview':return truncatePrompt(processedPrompt,100);case'truncated':if(isExpanded)return processedPrompt;return truncatePrompt(processedPrompt,preferences.truncationLength);case'compact':return truncatePrompt(processedPrompt,50);case'full':default:return processedPrompt;}},[processedPrompt,displayMode,isExpanded,preferences.truncationLength]);// Check if prompt is expected for this node type
const promptExpected=useMemo(()=>{return isPromptExpected(nodeData);},[nodeData]);// Get display status
const status=useMemo(()=>{return getPromptDisplayStatus(processedPrompt);},[processedPrompt]);// Generate accessibility information
const accessibility=useMemo(()=>{const description=processedPrompt?getPromptAccessibilityDescription(processedPrompt):'No generation prompt available';return{ariaLabel:`Generation prompt: ${description}`,ariaDescription:validation.isValid?`Valid prompt with ${(analysis===null||analysis===void 0?void 0:analysis.wordCount)||0} words`:`Invalid prompt: ${validation.issues.join(', ')}`,liveRegion:'',role:'region',ariaExpanded:displayMode==='truncated'?isExpanded:undefined};},[processedPrompt,validation,analysis,displayMode,isExpanded]);// Copy to clipboard with enhanced feedback
const copyToClipboard=useCallback(async()=>{if(!processedPrompt){setError({type:'clipboard',message:'No prompt available to copy',timestamp:new Date(),recoverable:false});return false;}setCopyStatus('copying');try{const success=await copyPromptToClipboard(processedPrompt);if(success){setCopyStatus('success');announceToScreenReader('Prompt copied to clipboard');// Reset status after delay
if(copyTimeoutRef.current){clearTimeout(copyTimeoutRef.current);}copyTimeoutRef.current=setTimeout(()=>{setCopyStatus('idle');},2000);return true;}else{throw new Error('Clipboard operation failed');}}catch(err){setCopyStatus('error');setError({type:'clipboard',message:'Failed to copy prompt to clipboard',details:err instanceof Error?err.message:String(err),timestamp:new Date(),recoverable:true});// Reset status after delay
setTimeout(()=>setCopyStatus('idle'),2000);return false;}},[processedPrompt]);// Screen reader announcements - define early so other callbacks can use it
const announceToScreenReader=useCallback(message=>{if(!liveRegionRef.current){// Create live region if it doesn't exist
const liveRegion=document.createElement('div');liveRegion.setAttribute('aria-live','polite');liveRegion.setAttribute('aria-atomic','true');liveRegion.style.position='absolute';liveRegion.style.left='-10000px';liveRegion.style.width='1px';liveRegion.style.height='1px';liveRegion.style.overflow='hidden';document.body.appendChild(liveRegion);liveRegionRef.current=liveRegion;}liveRegionRef.current.textContent=message;if(config.debug){console.debug('[useGenerationPrompt] Screen reader announcement:',message);}},[config.debug]);// Toggle expanded state
const toggleExpanded=useCallback(()=>{setIsExpanded(prev=>{const newState=!prev;announceToScreenReader(newState?'Prompt expanded':'Prompt collapsed');return newState;});},[announceToScreenReader]);// Change display mode with preference persistence
const handleSetDisplayMode=useCallback(mode=>{setDisplayMode(mode);setPreferences({...preferences,defaultDisplayMode:mode});announceToScreenReader(`Display mode changed to ${mode}`);},[setPreferences,preferences,announceToScreenReader]);// Clear error state
const clearError=useCallback(()=>{setError(null);announceToScreenReader('Error cleared');},[announceToScreenReader]);// Refresh analysis and validation
const refresh=useCallback(()=>{setRenderCount(prev=>prev+1);// Force re-computation by clearing any memoized values
if(config.debug){console.debug('[useGenerationPrompt] Refreshing analysis and validation');}},[config.debug]);// Future editing functions (stubs for now) - using stable callbacks to avoid hook rules violation
const startEditing=useCallback(()=>{if(!config.enableEditing||!editState)return;setEditState(prev=>prev?{...prev,isEditing:true,draft:processedPrompt,hasChanges:false}:null);},[config.enableEditing,editState,processedPrompt]);const saveEdit=useCallback(async newPrompt=>{if(!config.enableEditing||!editState)return false;setEditState(prev=>prev?{...prev,saving:true}:null);// TODO: Implement actual save logic
await new Promise(resolve=>setTimeout(resolve,1000));setEditState(prev=>prev?{...prev,isEditing:false,saving:false,hasChanges:false}:null);return true;},[config.enableEditing,editState]);const cancelEdit=useCallback(()=>{if(!config.enableEditing||!editState)return;setEditState(prev=>prev?{...prev,isEditing:false,draft:'',hasChanges:false}:null);},[config.enableEditing,editState]);// Update render count for metrics
useEffect(()=>{setRenderCount(prev=>prev+1);setMetrics(prev=>({...prev,renderCount:prev.renderCount+1}));},[displayPrompt]);// Auto-expand logic based on preferences
useEffect(()=>{if(preferences.autoExpand&&analysis&&analysis.wordCount<100){setIsExpanded(true);}},[preferences.autoExpand,analysis]);// Cleanup on unmount
useEffect(()=>{return()=>{const abortController=abortControllerRef.current;const copyTimeout=copyTimeoutRef.current;const liveRegion=liveRegionRef.current;if(abortController){abortController.abort();}if(copyTimeout){clearTimeout(copyTimeout);}if(liveRegion&&document.body.contains(liveRegion)){document.body.removeChild(liveRegion);}};},[]);// Debug information (development only)
const debug=config.debug?{nodeId:(nodeData===null||nodeData===void 0?void 0:nodeData.nodeId)||'unknown',promptLength:processedPrompt.length,analysisCache:!!analysis,renderCount,lastUpdate:new Date()}:undefined;return{// Core prompt data
rawPrompt,processedPrompt,displayPrompt,hasValidPrompt:validation.isValid&&!!processedPrompt,isPromptExpected:promptExpected,// State management
displayMode,isExpanded,copyStatus,error,isLoading:false,// Always false for now, can be extended later
// Analysis and validation
analysis,status,validation,// Actions and operations
copyToClipboard,toggleExpanded,setDisplayMode:handleSetDisplayMode,clearError,refresh,// Editing support
editState,startEditing:config.enableEditing?startEditing:undefined,saveEdit:config.enableEditing?saveEdit:undefined,cancelEdit:config.enableEditing?cancelEdit:undefined,// Accessibility
accessibility,announceToScreenReader,// Performance and debugging
metrics,debug};}// ============================================================================
// Additional Utility Hooks
// ============================================================================
/**
 * Hook for managing multiple prompts efficiently
 *
 * @param nodeDataList - Array of node data objects
 * @param options - Shared options for all prompts
 * @returns Array of prompt management interfaces
 *
 * @example
 * ```typescript
 * const prompts = useMultipleGenerationPrompts(nodeList, { debug: true });
 * ```
 */export function useMultipleGenerationPrompts(nodeDataList){let options=arguments.length>1&&arguments[1]!==undefined?arguments[1]:{};// Create individual hooks for each node to comply with React hooks rules
const results=[];for(let i=0;i<nodeDataList.length;i++){// eslint-disable-next-line react-hooks/rules-of-hooks
const result=useGenerationPrompt(nodeDataList[i],options);results.push(result);}return results;}/**
 * Hook for prompt-specific error boundary integration
 * 
 * @param onError - Error handler function
 * @returns Error boundary props and controls
 */export function usePromptErrorBoundary(onError){const[hasError,setHasError]=useState(false);const[lastError,setLastError]=useState(null);const resetError=useCallback(()=>{setHasError(false);setLastError(null);},[]);const catchError=useCallback(error=>{setHasError(true);setLastError(error);onError===null||onError===void 0?void 0:onError(error);},[onError]);return{hasError,lastError,resetError,catchError,errorBoundaryProps:{onError:catchError,fallback:hasError}};}// ============================================================================
// Type Exports for External Use
// ============================================================================
// All types are already exported above as part of their declarations
// Additional aliases for external use
// ============================================================================
// Unit Test Examples (as comments)
// ============================================================================
/*
// Example unit tests for useGenerationPrompt hook

import { renderHook, act } from '@testing-library/react';
import { useGenerationPrompt } from './useGenerationPrompt';

describe('useGenerationPrompt', () => {
  const mockNodeData: ContentNodeData = {
    id: 'test-node',
    generationPrompt: 'Test prompt for generation',
    type: 'lesson'
  };

  it('should initialize with valid prompt data', () => {
    const { result } = renderHook(() => useGenerationPrompt(mockNodeData));
    
    expect(result.current.hasValidPrompt).toBe(true);
    expect(result.current.processedPrompt).toBe('Test prompt for generation');
    expect(result.current.displayMode).toBe('truncated');
  });

  it('should handle empty prompt gracefully', () => {
    const emptyNodeData = { ...mockNodeData, generationPrompt: '' };
    const { result } = renderHook(() => useGenerationPrompt(emptyNodeData));
    
    expect(result.current.hasValidPrompt).toBe(false);
    expect(result.current.validation.isValid).toBe(false);
  });

  it('should copy prompt to clipboard successfully', async () => {
    Object.assign(navigator, {
      clipboard: {
        writeText: jest.fn().mockResolvedValue(true)
      }
    });

    const { result } = renderHook(() => useGenerationPrompt(mockNodeData));
    
    await act(async () => {
      const success = await result.current.copyToClipboard();
      expect(success).toBe(true);
      expect(result.current.copyStatus).toBe('success');
    });
  });

  it('should toggle expanded state correctly', () => {
    const { result } = renderHook(() => useGenerationPrompt(mockNodeData));
    
    act(() => {
      result.current.toggleExpanded();
    });
    
    expect(result.current.isExpanded).toBe(true);
  });

  it('should validate prompt correctly', () => {
    const invalidNodeData = { ...mockNodeData, generationPrompt: null };
    const { result } = renderHook(() => useGenerationPrompt(invalidNodeData));
    
    expect(result.current.validation.isValid).toBe(false);
    expect(result.current.validation.issues).toContain('No prompt provided');
  });

  it('should analyze prompt characteristics', () => {
    const { result } = renderHook(() => useGenerationPrompt(mockNodeData));
    
    expect(result.current.analysis).toBeTruthy();
    expect(result.current.analysis?.wordCount).toBeGreaterThan(0);
    expect(result.current.analysis?.format).toBe('plain');
  });

  it('should handle display mode changes', () => {
    const { result } = renderHook(() => useGenerationPrompt(mockNodeData));
    
    act(() => {
      result.current.setDisplayMode('full');
    });
    
    expect(result.current.displayMode).toBe('full');
  });

  it('should provide accessibility information', () => {
    const { result } = renderHook(() => useGenerationPrompt(mockNodeData));
    
    expect(result.current.accessibility.ariaLabel).toContain('Generation prompt');
    expect(result.current.accessibility.role).toBe('region');
  });

  it('should handle errors gracefully', async () => {
    Object.assign(navigator, {
      clipboard: {
        writeText: jest.fn().mockRejectedValue(new Error('Clipboard error'))
      }
    });

    const { result } = renderHook(() => useGenerationPrompt(mockNodeData));
    
    await act(async () => {
      const success = await result.current.copyToClipboard();
      expect(success).toBe(false);
      expect(result.current.error?.type).toBe('clipboard');
    });
  });

  it('should optimize performance with debouncing', () => {
    const { result, rerender } = renderHook(
      ({ nodeData }) => useGenerationPrompt(nodeData, { debounceDelay: 100 }),
      { initialProps: { nodeData: mockNodeData } }
    );

    // Change prompt multiple times quickly
    const nodeData1 = { ...mockNodeData, generationPrompt: 'Prompt 1' };
    const nodeData2 = { ...mockNodeData, generationPrompt: 'Prompt 2' };
    
    rerender({ nodeData: nodeData1 });
    rerender({ nodeData: nodeData2 });
    
    // Should still be processing the original prompt due to debouncing
    expect(result.current.processedPrompt).toBe('Test prompt for generation');
  });
});

// Integration tests with ContentNode component
describe('useGenerationPrompt integration', () => {
  it('should integrate properly with ContentNode component', () => {
    // Test integration with actual ContentNode component
    // This would require mounting the ContentNode with test data
    // and verifying the hook behaves correctly in context
  });

  it('should persist preferences across sessions', () => {
    // Test localStorage persistence of user preferences
  });

  it('should handle concurrent operations safely', () => {
    // Test multiple simultaneous copy operations
  });
});
*/